import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { localToday } from '../utils/dateUtils';
import { emitTareaChanged, onTareaChanged } from '../utils/tareasBus';
import { onEntidadChanged } from '../utils/entidadBus';
import { onDataChanged } from '../utils/dataBus';
import ExpedienteDetalle from './ExpedienteDetalle';
import TramiteDetalle from './TramiteDetalle';
import TareaDetalle from '../components/TareaDetalle';
import EventoModal from '../components/EventoModal';
import TareaModal from '../components/TareaModal';
import AudienciasPanel from './AudienciasPanel';
import VencimientosPanel from './VencimientosPanel';
import TareasPanel from './TareasPanel';
import HistorialPanel from './HistorialPanel';

const MANANA_HOURS = [0,1,2,3,4,5,6,7,8,9,10,11,12,13];
const TARDE_HOURS  = [14,15,16,17,18,19,20,21,22,23];

function buildSlots(baseHours) {
  return baseHours.map(h => `${String(h).padStart(2, '0')}:00`);
}

export default function Agenda() {
  const [tab,          setTab]          = useState('calendario');
  const [calVista,     setCalVista]     = useState('mes');
  const [eventos,      setEventos]      = useState([]);
  const [tareas,       setTareas]       = useState([]);
  const [, setLoading] = useState(true);
  const [fechaActual,  setFechaActual]  = useState(new Date());
  const [modalOpen,    setModalOpen]    = useState(false);
  const [eventoEditing, setEventoEditing] = useState(null);
  const [nuevaFecha,   setNuevaFecha]   = useState('');
  const [tareaModalOpen, setTareaModalOpen] = useState(false);
  const [nuevaTareaFecha, setNuevaTareaFecha] = useState('');
  const [toast,        setToast]        = useState(null);
  const [filtroTipo,   setFiltroTipo]   = useState('');
  const [diaSeleccionado, setDiaSeleccionado] = useState(localToday());
  const [verExpediente,   setVerExpediente]   = useState(null);
  const [verTramite,      setVerTramite]      = useState(null);
  const [tareaModal,      setTareaModal]      = useState(null);
  const [tareaEdit,       setTareaEdit]       = useState(null);
  const [bloquesRecurrentes,  setBloquesRecurrentes]  = useState([]);
  const [modalBloquesOpen,    setModalBloquesOpen]    = useState(false);
  const [editingBloqueId,     setEditingBloqueId]     = useState(null);
  const [formBloque, setFormBloque] = useState({ titulo: '', dia_semana: 2, hora_inicio: '08:00', hora_fin: '09:00', color: '#6366f1', activo: true });
  const [guardandoBloque, setGuardandoBloque] = useState(false);

  useEffect(() => { fetchEventos(); fetchTareas(); fetchBloquesRecurrentes(); }, []);

  // Mantenerse sincronizado cuando una tarea cambia desde otro lugar
  useEffect(() => onTareaChanged(() => fetchTareas()), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Idem cuando se elimina/edita un expediente, trámite o cliente vinculado
  useEffect(() => onEntidadChanged(() => { fetchEventos(); fetchTareas(); }), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Automático: cualquier escritura sobre agenda, tareas, expedientes, trámites o clientes
  useEffect(() => onDataChanged(() => { fetchEventos(); fetchTareas(); }, ['agenda', 'tareas', 'expedientes', 'tramites', 'clientes']), []); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchEventos() {
    setLoading(true);
    const { data } = await supabase.from('agenda')
      .select('*, clientes(nombre, apellido), expedientes(numero, caratula)')
      .eq('eliminado', false)
      .order('fecha', { ascending: true });
    setEventos(data || []);
    setLoading(false);
  }

  async function fetchTareas() {
    const { data } = await supabase.from('tareas')
      .select('*, expedientes(caratula, numero, expedientes_clientes(clientes(nombre, apellido))), tramites(nombre, tramites_clientes(clientes(nombre, apellido)))')
      .eq('completada', false).eq('eliminado', false).order('created_at', { ascending: true });
    setTareas(data || []);
  }

  async function fetchBloquesRecurrentes() {
    const { data } = await supabase.from('bloques_recurrentes').select('*').order('dia_semana').order('hora_inicio');
    setBloquesRecurrentes(data || []);
  }

  async function saveBloque() {
    if (!formBloque.titulo.trim()) { showToast('El título es obligatorio'); return; }
    if (formBloque.hora_inicio >= formBloque.hora_fin) { showToast('La hora de fin debe ser mayor a la de inicio'); return; }
    setGuardandoBloque(true);
    const payload = { titulo: formBloque.titulo.trim(), dia_semana: Number(formBloque.dia_semana), hora_inicio: formBloque.hora_inicio, hora_fin: formBloque.hora_fin, color: formBloque.color, activo: formBloque.activo };
    if (editingBloqueId) {
      await supabase.from('bloques_recurrentes').update(payload).eq('id', editingBloqueId);
    } else {
      await supabase.from('bloques_recurrentes').insert(payload);
    }
    setGuardandoBloque(false);
    setEditingBloqueId(null);
    setFormBloque({ titulo: '', dia_semana: 2, hora_inicio: '08:00', hora_fin: '09:00', color: '#6366f1', activo: true });
    fetchBloquesRecurrentes();
    showToast(editingBloqueId ? 'Bloque actualizado ✓' : 'Bloque creado ✓');
  }

  async function deleteBloque(id) {
    await supabase.from('bloques_recurrentes').delete().eq('id', id);
    fetchBloquesRecurrentes();
    showToast('Bloque eliminado');
  }

  function getBloquesForDia(fechaStr) {
    const d = new Date(fechaStr + 'T12:00:00');
    const jsDay = d.getDay();
    const isoDay = jsDay === 0 ? 7 : jsDay;
    return bloquesRecurrentes.filter(b => b.activo && b.dia_semana === isoDay);
  }

  function bloquesEnSlot(slot, bloquesDelDia) {
    const h = parseInt(slot.substring(0, 2));
    const slotFin = String(h + 1).padStart(2, '0') + ':00';
    return bloquesDelDia.filter(b => b.hora_inicio < slotFin && b.hora_fin > slot);
  }

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(null), 3000); }

  function openNew(fechaPre = '') {
    setEventoEditing(null);
    setNuevaFecha(fechaPre || diaSeleccionado);
    setModalOpen(true);
  }

  function openEdit(ev) {
    setEventoEditing(ev);
    setModalOpen(true);
  }

  function openNewTarea(fechaPre = '') {
    setNuevaTareaFecha(fechaPre || diaSeleccionado);
    setTareaModalOpen(true);
  }

  async function completarTarea(id) {
    await supabase.from('tareas').update({ completada: true, completada_en: new Date().toISOString() }).eq('id', id);
    fetchTareas();
    emitTareaChanged({ id, completada: true });
  }

  function navDia(delta) {
    const d = new Date(diaSeleccionado + 'T12:00:00');
    d.setDate(d.getDate() + delta);
    setDiaSeleccionado(d.toISOString().split('T')[0]);
  }

  const tareasConFecha = tareas.filter(t => t.fecha || t.fecha_vencimiento);

  const getItemsDia = (fechaStr) => ({
    eventos: eventos.filter(e => e.fecha === fechaStr && (!filtroTipo || e.tipo === filtroTipo)),
    tareas: tareasConFecha.filter(t => t.fecha === fechaStr || t.fecha_vencimiento === fechaStr)
  });

  const getDiasDelMes = () => {
    const año = fechaActual.getFullYear(), mes = fechaActual.getMonth();
    const primerDia = new Date(año, mes, 1).getDay();
    const diasEnMes = new Date(año, mes + 1, 0).getDate();
    const dias = [];
    for (let i = 0; i < (primerDia === 0 ? 6 : primerDia - 1); i++) dias.push(null);
    for (let i = 1; i <= diasEnMes; i++) dias.push(i);
    return dias;
  };

  const getEventosSemana = () => {
    const inicio = new Date(fechaActual);
    const dia = inicio.getDay();
    inicio.setDate(inicio.getDate() - (dia === 0 ? 6 : dia - 1));
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(inicio); d.setDate(inicio.getDate() + i); return d; });
  };

  const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const diasSemana = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'];

  const colorTipo = (tipo) => {
    const map = { 'Audiencia': '#0d9488', 'Vencimiento procesal': '#ea580c', 'Consulta / Turno': '#0891b2', 'Reunión': '#4f46e5', 'Vencimiento de deuda': '#ea580c' };
    return map[tipo] || '#9ca3af';
  };

  const today = localToday();
  const eventosDiaSelec = eventos.filter(e => e.fecha === diaSeleccionado && (!filtroTipo || e.tipo === filtroTipo));
  const tareasDiaSelec  = tareasConFecha.filter(t => t.fecha === diaSeleccionado || t.fecha_vencimiento === diaSeleccionado);
  const formatDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('es-AR') : '—';
  const prioridadColor = (p) => ({ urgente: 'var(--red)', normal: 'var(--blue)', baja: 'var(--text4)' }[p] || 'var(--blue)');

  const mananaSlots = buildSlots(MANANA_HOURS);
  const tardeSlots  = buildSlots(TARDE_HOURS);
  const allDaySlots = [...mananaSlots, ...tardeSlots, '24:00'];

  const eventsForSlot = (slot) => {
    const idx = allDaySlots.indexOf(slot);
    const nextSlot = allDaySlots[idx + 1] || '24:00';
    return eventosDiaSelec.filter(e => {
      if (!e.hora) return false;
      const h = e.hora.substring(0, 5);
      return h >= slot && h < nextSlot;
    });
  };

  const eventosSinHora = eventosDiaSelec.filter(e => !e.hora);

  const TABS = [
    { id: 'calendario',   label: 'Calendario' },
    { id: 'audiencias',   label: 'Audiencias' },
    { id: 'vencimientos', label: 'Vencimientos' },
    { id: 'tareas',       label: 'Tareas agendadas' },
    { id: 'historial',    label: 'Registro de tareas y audiencias completadas' },
  ];

  return (
    <div className="page">
      {verExpediente && (
        <>
          <div className="drawer-overlay" onMouseDown={() => setVerExpediente(null)} />
          <div className="drawer">
            <ExpedienteDetalle expedienteId={verExpediente} onClose={() => setVerExpediente(null)} backLabel="← Volver a Agenda" />
          </div>
        </>
      )}
      {verTramite && (
        <>
          <div className="drawer-overlay" onMouseDown={() => setVerTramite(null)} />
          <div className="drawer">
            <TramiteDetalle tramiteId={verTramite} onClose={() => setVerTramite(null)} backLabel="← Volver a Agenda" />
          </div>
        </>
      )}

      <div className="page-topbar">
        <span className="page-title">Agenda y Calendario</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary btn-sm" onClick={() => openNewTarea()}>+ Nueva tarea</button>
          <button className="btn btn-primary btn-sm" onClick={() => openNew()}>+ Nuevo evento</button>
        </div>
      </div>

      <div className="page-content">

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border2)', marginBottom: 0 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: '10px 16px', fontSize: 13,
              fontWeight: tab === t.id ? 600 : 400,
              color: tab === t.id ? 'var(--blue)' : 'var(--text3)',
              background: 'none', border: 'none',
              borderBottom: tab === t.id ? '2px solid var(--blue)' : '2px solid transparent',
              cursor: 'pointer', marginBottom: -1, whiteSpace: 'nowrap',
            }}>{t.label}</button>
          ))}
        </div>

        {/* Controles del calendario — fila fija debajo de los tabs, oculta en otras pestañas */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0', marginBottom: 14, borderBottom: '1px solid var(--border2)', visibility: tab === 'calendario' ? 'visible' : 'hidden', pointerEvents: tab === 'calendario' ? 'auto' : 'none' }}>
          {[{ id: 'mes', label: 'Mes' }, { id: 'semana', label: 'Semana' }, { id: 'dia', label: 'Día' }].map(v => (
            <button key={v.id} className={`btn ${calVista === v.id ? 'btn-primary' : 'btn-ghost'} btn-sm`} onClick={() => setCalVista(v.id)}>{v.label}</button>
          ))}
          <span style={{ color: 'var(--border2)', margin: '0 2px' }}>|</span>
          {calVista === 'mes' && <>
            <button className="btn btn-ghost btn-sm" onClick={() => { const d = new Date(fechaActual); d.setMonth(d.getMonth() - 1); setFechaActual(d); }}>←</button>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', minWidth: 120, textAlign: 'center' }}>{meses[fechaActual.getMonth()]} {fechaActual.getFullYear()}</span>
            <button className="btn btn-ghost btn-sm" onClick={() => { const d = new Date(fechaActual); d.setMonth(d.getMonth() + 1); setFechaActual(d); }}>→</button>
            {(fechaActual.getMonth() !== new Date().getMonth() || fechaActual.getFullYear() !== new Date().getFullYear()) && (
              <button className="btn btn-ghost btn-sm" onClick={() => { setFechaActual(new Date()); setDiaSeleccionado(today); }}>Hoy</button>
            )}
          </>}
          {calVista === 'semana' && <>
            <button className="btn btn-ghost btn-sm" onClick={() => { const d = new Date(fechaActual); d.setDate(d.getDate() - 7); setFechaActual(d); }}>←</button>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text2)', minWidth: 150, textAlign: 'center' }}>Sem. del {getEventosSemana()[0].toLocaleDateString('es-AR')}</span>
            <button className="btn btn-ghost btn-sm" onClick={() => { const d = new Date(fechaActual); d.setDate(d.getDate() + 7); setFechaActual(d); }}>→</button>
            {!getEventosSemana().some(d => d.toISOString().split('T')[0] === today) && (
              <button className="btn btn-ghost btn-sm" onClick={() => setFechaActual(new Date())}>Hoy</button>
            )}
          </>}
          {calVista === 'dia' && <>
            <button className="btn btn-ghost btn-sm" onClick={() => navDia(-1)}>←</button>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', minWidth: 190, textAlign: 'center', textTransform: 'capitalize' }}>
              {new Date(diaSeleccionado + 'T12:00:00').toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </span>
            <button className="btn btn-ghost btn-sm" onClick={() => navDia(1)}>→</button>
            {diaSeleccionado !== today && <button className="btn btn-ghost btn-sm" onClick={() => setDiaSeleccionado(today)}>Hoy</button>}
            <button className="btn btn-ghost btn-sm" onClick={() => openNew(diaSeleccionado)}>+ Evento</button>
          </>}
          <span style={{ color: 'var(--border2)', margin: '0 2px' }}>|</span>
          <select className="filter-select" value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
            <option value="">Todos los tipos</option>
            <option>Audiencia</option>
            <option>Vencimiento procesal</option>
            <option>Consulta / Turno</option>
            <option>Reunión</option>
            <option>Vencimiento de deuda</option>
          </select>
        </div>

        {/* ── CALENDARIO ── */}
        {tab === 'calendario' && (
          <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 220px)', overflow: 'hidden' }}>

            {/* MES */}
            {calVista === 'mes' && (() => {
              const diasDelMes = getDiasDelMes();
              const numRows = diasDelMes.length / 7;
              return (
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, marginBottom: 4 }}>
                  {diasSemana.map(d => <div key={d} style={{ textAlign: 'center', fontSize: 10, color: 'var(--text4)', padding: '3px 0', fontWeight: 600 }}>{d}</div>)}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridTemplateRows: `repeat(${numRows}, 1fr)`, gap: 3, flex: 1, overflow: 'hidden' }}>
                  {diasDelMes.map((dia, i) => {
                    const fechaStr = dia ? `${fechaActual.getFullYear()}-${String(fechaActual.getMonth() + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}` : '';
                    const { eventos: evsDia, tareas: tareasDia } = dia ? getItemsDia(fechaStr) : { eventos: [], tareas: [] };
                    const esHoy = fechaStr === today;
                    const esSeleccionado = fechaStr === diaSeleccionado;
                    const audiencias = evsDia.filter(e => e.tipo === 'Audiencia').length;
                    const vencimientosEv = evsDia.filter(e => e.tipo.startsWith('Vencimiento')).length;
                    const otros = evsDia.filter(e => e.tipo !== 'Audiencia' && !e.tipo.startsWith('Vencimiento')).length;
                    const tareasVenc = tareasDia.filter(t => t.fecha_vencimiento === fechaStr).length;
                    const tareasSolo = tareasDia.filter(t => t.fecha_vencimiento !== fechaStr).length;
                    const bloquesDia = dia ? getBloquesForDia(fechaStr) : [];
                    return (
                      <div
                        key={i}
                        onClick={() => { if (!dia) return; setDiaSeleccionado(fechaStr); setCalVista('dia'); }}
                        style={{ background: dia ? 'var(--surface)' : 'transparent', border: `1px solid ${esHoy || esSeleccionado ? 'var(--blue)' : dia ? 'var(--border2)' : 'transparent'}`, borderRadius: 6, padding: '6px 5px', overflow: 'hidden', cursor: dia ? 'pointer' : 'default' }}
                      >
                        {dia && (
                          <>
                            <div style={{ fontSize: 12, fontWeight: esHoy ? 700 : 400, color: esHoy ? '#fff' : 'var(--text2)', background: esHoy ? 'var(--blue)' : 'transparent', borderRadius: 4, width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 5 }}>{dia}</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {bloquesDia.map(b => (
                                <span key={b.id} style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: b.color + '18', color: b.color, fontWeight: 500, fontStyle: 'italic' }}>↻ {b.hora_inicio.substring(0,5)}</span>
                              ))}
                              {audiencias > 0 && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: '#ccfbf1', color: '#0d9488', fontWeight: 500 }}>{audiencias} audiencia{audiencias > 1 ? 's' : ''}</span>}
                              {(vencimientosEv + tareasVenc) > 0 && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: '#ffedd5', color: '#ea580c', fontWeight: 500 }}>{vencimientosEv + tareasVenc} vencimiento{(vencimientosEv + tareasVenc) > 1 ? 's' : ''}</span>}
                              {otros > 0 && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: '#f1f5f9', color: '#475569', fontWeight: 500 }}>{otros} evento{otros > 1 ? 's' : ''}</span>}
                              {tareasSolo > 0 && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: 'var(--blue-light)', color: 'var(--blue)', fontWeight: 500 }}>{tareasSolo} tarea{tareasSolo > 1 ? 's' : ''}</span>}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              );
            })()}

            {/* SEMANA */}
            {calVista === 'semana' && (
              <div style={{ height: '100%', overflow: 'auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
                  {getEventosSemana().map((dia, i) => {
                    const fechaStr = dia.toISOString().split('T')[0];
                    const { eventos: evsDia, tareas: tareasDia } = getItemsDia(fechaStr);
                    const esHoy = fechaStr === today;
                    return (
                      <div key={i} style={{ background: 'var(--surface)', border: `1px solid ${esHoy ? 'var(--blue)' : 'var(--border2)'}`, borderRadius: 8, padding: 10, minHeight: 110 }}>
                        <div style={{ fontSize: 10, color: 'var(--text4)', marginBottom: 3 }}>{diasSemana[i]}</div>
                        <div style={{ fontSize: 18, fontWeight: 600, color: esHoy ? 'var(--blue)' : 'var(--text)', marginBottom: 6 }}>{dia.getDate()}</div>
                        {getBloquesForDia(fechaStr).map(b => (
                          <div key={b.id} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, marginBottom: 3, background: b.color + '18', color: b.color, borderLeft: `2px solid ${b.color}`, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', fontStyle: 'italic' }}>
                            ↻ {b.hora_inicio.substring(0,5)}–{b.hora_fin.substring(0,5)} {b.titulo}
                          </div>
                        ))}
                        {evsDia.map(ev => (
                          <div key={ev.id} onClick={() => openEdit(ev)} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, marginBottom: 3, background: colorTipo(ev.tipo) + '18', color: colorTipo(ev.tipo), cursor: 'pointer', borderLeft: `2px solid ${colorTipo(ev.tipo)}`, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                            {ev.hora && <span style={{ opacity: 0.7 }}>{ev.hora.substring(0,5)} </span>}{ev.titulo}
                          </div>
                        ))}
                        {tareasDia.map(t => (
                          <div key={t.id} onClick={() => setTareaModal(t)} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, marginBottom: 3, background: 'var(--blue-light)', color: 'var(--blue)', borderLeft: '2px solid var(--blue)', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', cursor: 'pointer' }}>
                            ✓ {t.titulo?.substring(0, 18)}
                          </div>
                        ))}
                        <button onClick={() => { setDiaSeleccionado(fechaStr); openNew(fechaStr); }} style={{ fontSize: 10, color: 'var(--text4)', background: 'none', border: 'none', cursor: 'pointer', marginTop: 3 }}>+ agregar</button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* DÍA */}
            {calVista === 'dia' && (() => {
              const bloquesDia = getBloquesForDia(diaSeleccionado);
              return (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 220px', gap: 12, alignItems: 'flex-start' }}>

                  {/* MAÑANA */}
                  <div style={{ background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 10, overflow: 'hidden' }}>
                    <div style={{ padding: '8px 14px', background: 'var(--surface2)', borderBottom: '1px solid var(--border2)', fontSize: 11, fontWeight: 700, color: 'var(--text4)', letterSpacing: 1 }}>MAÑANA</div>
                    {mananaSlots.map(slot => {
                      const evs = eventsForSlot(slot);
                      const bloques = bloquesEnSlot(slot, bloquesDia);
                      const esMadrugada = slot < '07:00';
                      const tieneContenido = evs.length > 0 || bloques.length > 0;
                      const slotH = tieneContenido ? undefined : esMadrugada ? 16 : 24;
                      return (
                        <div key={slot} style={{ display: 'flex', gap: 0, minHeight: slotH, borderBottom: '1px solid var(--border2)', background: bloques.length > 0 ? bloques[0].color + '0c' : undefined }}>
                          <div style={{ width: 44, padding: tieneContenido ? '5px 8px' : esMadrugada ? '2px 8px' : '3px 8px', fontSize: esMadrugada ? 9 : 10, color: 'var(--text4)', fontFamily: 'DM Mono', flexShrink: 0, borderRight: '1px solid var(--border2)', background: esMadrugada ? 'var(--surface2)' : 'transparent', display: 'flex', alignItems: 'flex-start' }}>
                            {slot}
                          </div>
                          <div style={{ flex: 1, padding: tieneContenido ? '4px 8px' : '2px 6px', display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {bloques.map(b => <SlotBloque key={b.id} bloque={b} />)}
                            {evs.map(ev => <SlotEvento key={ev.id} ev={ev} colorTipo={colorTipo} onEdit={openEdit} onExpediente={setVerExpediente} />)}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* TARDE */}
                  <div style={{ background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 10, overflow: 'hidden' }}>
                    <div style={{ padding: '8px 14px', background: 'var(--surface2)', borderBottom: '1px solid var(--border2)', fontSize: 11, fontWeight: 700, color: 'var(--text4)', letterSpacing: 1 }}>TARDE</div>
                    {tardeSlots.map(slot => {
                      const evs = eventsForSlot(slot);
                      const bloques = bloquesEnSlot(slot, bloquesDia);
                      const tieneContenido = evs.length > 0 || bloques.length > 0;
                      return (
                        <div key={slot} style={{ display: 'flex', gap: 0, minHeight: tieneContenido ? undefined : 24, borderBottom: '1px solid var(--border2)', background: bloques.length > 0 ? bloques[0].color + '0c' : undefined }}>
                          <div style={{ width: 44, padding: tieneContenido ? '5px 8px' : '3px 8px', fontSize: 10, color: 'var(--text4)', fontFamily: 'DM Mono', flexShrink: 0, borderRight: '1px solid var(--border2)', display: 'flex', alignItems: 'flex-start' }}>
                            {slot}
                          </div>
                          <div style={{ flex: 1, padding: tieneContenido ? '4px 8px' : '2px 6px', display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {bloques.map(b => <SlotBloque key={b.id} bloque={b} />)}
                            {evs.map(ev => <SlotEvento key={ev.id} ev={ev} colorTipo={colorTipo} onEdit={openEdit} onExpediente={setVerExpediente} />)}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* SIN HORA */}
                  <div style={{ background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 10, overflow: 'hidden' }}>
                    <div style={{ padding: '8px 14px', background: 'var(--surface2)', borderBottom: '1px solid var(--border2)', fontSize: 11, fontWeight: 700, color: 'var(--text4)', letterSpacing: 1 }}>SIN HORA</div>
                    <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {eventosSinHora.length === 0 && tareasDiaSelec.length === 0 ? (
                        <div style={{ fontSize: 11, color: 'var(--text4)', padding: '16px 0', textAlign: 'center' }}>Sin items</div>
                      ) : (
                        <>
                          {eventosSinHora.map(ev => <SlotEvento key={ev.id} ev={ev} colorTipo={colorTipo} onEdit={openEdit} onExpediente={setVerExpediente} sinHora />)}
                          {tareasDiaSelec.map(t => <SlotTarea key={t.id} tarea={t} prioridadColor={prioridadColor} onCompletar={completarTarea} onExpediente={setVerExpediente} onTramite={setVerTramite} onVerTarea={setTareaModal} formatDate={formatDate} today={today} />)}
                        </>
                      )}
                    </div>
                  </div>

                </div>
              );
            })()}
          </div>
        )}

        {/* ── AUDIENCIAS ── */}
        {tab === 'audiencias' && (
          <AudienciasPanel
            mode="page"
            onOpenEdit={openEdit}
            onNavigateExpediente={setVerExpediente}
          />
        )}

        {/* ── VENCIMIENTOS ── */}
        {tab === 'vencimientos' && (
          <VencimientosPanel
            mode="page"
            onOpenEdit={openEdit}
            onVerTarea={setTareaModal}
            onNavigateExpediente={setVerExpediente}
          />
        )}

        {/* ── TAREAS ── */}
        {tab === 'tareas' && (
          <TareasPanel
            mode="page"
            onVerTarea={setTareaModal}
            onCompletar={async (id) => {
              await supabase.from('tareas').update({ completada: true, completada_en: new Date().toISOString() }).eq('id', id);
              fetchTareas();
            }}
            onNavigateExpediente={setVerExpediente}
            onNavigateTramite={setVerTramite}
          />
        )}

        {/* ── HISTORIAL ── */}
        {tab === 'historial' && (
          <HistorialPanel
            mode="page"
            onVerTarea={setTareaModal}
            onOpenEdit={openEdit}
            onNavigateExpediente={setVerExpediente}
            onNavigateTramite={setVerTramite}
          />
        )}

      </div>

      {/* MODAL EVENTO */}
      <EventoModal
        open={modalOpen}
        editing={eventoEditing}
        defaultFecha={nuevaFecha}
        onClose={() => setModalOpen(false)}
        onSaved={() => { fetchEventos(); showToast('Evento guardado ✓'); }}
      />

      {/* MODAL TAREA */}
      <TareaModal
        open={tareaModalOpen}
        defaultFecha={nuevaTareaFecha}
        onClose={() => setTareaModalOpen(false)}
        onSaved={() => { fetchTareas(); showToast('Tarea guardada ✓'); }}
      />

      {/* MODAL EDICIÓN TAREA */}
      <TareaModal
        open={!!tareaEdit}
        editing={tareaEdit}
        onClose={() => setTareaEdit(null)}
        onSaved={() => { setTareaEdit(null); fetchTareas(); showToast('Tarea actualizada ✓'); }}
      />

      {/* MODAL FICHA TAREA */}
      {tareaModal && (
        <TareaDetalle
          tareaId={tareaModal.id}
          onClose={() => setTareaModal(null)}
          onCompletar={() => { fetchTareas(); setTareaModal(null); }}
          onEdit={(tarea) => { setTareaModal(null); setTareaEdit(tarea); }}
          onNavigateExpediente={(id) => { setVerExpediente(id); setTareaModal(null); }}
          onNavigateTramite={(id) => { setVerTramite(id); setTareaModal(null); }}
        />
      )}

      {/* MODAL BLOQUES RECURRENTES */}
      {modalBloquesOpen && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setModalBloquesOpen(false)} style={{ zIndex: 700 }}>
          <div className="modal" style={{ width: 560, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header" style={{ flexShrink: 0 }}>
              <div className="modal-title">Bloques recurrentes</div>
              <button className="modal-close" onClick={() => setModalBloquesOpen(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ flex: 1, overflowY: 'auto' }}>
              <div style={{ background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text4)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>
                  {editingBloqueId ? 'Editar bloque' : 'Nuevo bloque'}
                </div>
                <div className="form-grid">
                  <div className="form-group form-full">
                    <label className="form-label">Nombre del bloque *</label>
                    <input className="form-input" value={formBloque.titulo} onChange={e => setFormBloque(f => ({ ...f, titulo: e.target.value }))} placeholder="Ej: Clases, Reunión de equipo..." autoFocus={!editingBloqueId} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Día de la semana</label>
                    <select className="form-select" value={formBloque.dia_semana} onChange={e => setFormBloque(f => ({ ...f, dia_semana: Number(e.target.value) }))}>
                      <option value={1}>Lunes</option><option value={2}>Martes</option><option value={3}>Miércoles</option>
                      <option value={4}>Jueves</option><option value={5}>Viernes</option><option value={6}>Sábado</option><option value={7}>Domingo</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ display: 'flex', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <label className="form-label">Desde</label>
                      <input className="form-input" type="time" value={formBloque.hora_inicio} onChange={e => setFormBloque(f => ({ ...f, hora_inicio: e.target.value }))} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label className="form-label">Hasta</label>
                      <input className="form-input" type="time" value={formBloque.hora_fin} onChange={e => setFormBloque(f => ({ ...f, hora_fin: e.target.value }))} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Color</label>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {['#6366f1','#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#9ca3af'].map(c => (
                        <div key={c} onClick={() => setFormBloque(f => ({ ...f, color: c }))} style={{ width: 24, height: 24, borderRadius: '50%', background: c, cursor: 'pointer', border: formBloque.color === c ? '3px solid var(--text)' : '3px solid transparent', flexShrink: 0 }} />
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
                  {editingBloqueId && (
                    <button className="btn btn-ghost btn-sm" onClick={() => { setEditingBloqueId(null); setFormBloque({ titulo: '', dia_semana: 2, hora_inicio: '08:00', hora_fin: '09:00', color: '#6366f1', activo: true }); }}>Cancelar</button>
                  )}
                  <button className="btn btn-primary btn-sm" onClick={saveBloque} disabled={guardandoBloque}>
                    {guardandoBloque ? 'Guardando...' : editingBloqueId ? 'Actualizar' : '+ Agregar bloque'}
                  </button>
                </div>
              </div>

              {bloquesRecurrentes.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text4)', fontSize: 13 }}>No hay bloques recurrentes configurados.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {['Lunes','Martes','Miércoles','Jueves','Viernes','Sábado','Domingo'].map((diaLabel, idx) => {
                    const diaN = idx + 1;
                    const bloquesDia = bloquesRecurrentes.filter(b => b.dia_semana === diaN);
                    if (bloquesDia.length === 0) return null;
                    return (
                      <div key={diaN}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text4)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{diaLabel}</div>
                        {bloquesDia.map(b => (
                          <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--surface)', border: `0.5px solid var(--border)`, borderLeft: `3px solid ${b.color}`, borderRadius: 8, marginBottom: 4 }}>
                            <div style={{ width: 10, height: 10, borderRadius: '50%', background: b.color, flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, fontWeight: 500, color: b.activo ? 'var(--text)' : 'var(--text4)', textDecoration: b.activo ? 'none' : 'line-through' }}>{b.titulo}</div>
                              <div style={{ fontSize: 11, color: 'var(--text4)', fontFamily: 'DM Mono' }}>{b.hora_inicio.substring(0,5)} – {b.hora_fin.substring(0,5)}</div>
                            </div>
                            <button className="btn btn-ghost btn-xs" onClick={async () => { await supabase.from('bloques_recurrentes').update({ activo: !b.activo }).eq('id', b.id); fetchBloquesRecurrentes(); }} style={{ fontSize: 10, color: b.activo ? '#10b981' : 'var(--text4)' }}>
                              {b.activo ? 'Activo' : 'Pausado'}
                            </button>
                            <button className="btn btn-ghost btn-xs" onClick={() => { setEditingBloqueId(b.id); setFormBloque({ titulo: b.titulo, dia_semana: b.dia_semana, hora_inicio: b.hora_inicio, hora_fin: b.hora_fin, color: b.color, activo: b.activo }); }}>Editar</button>
                            <button className="btn btn-danger btn-xs" onClick={() => deleteBloque(b.id)}>🗑</button>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ flexShrink: 0 }}>
              <button className="btn btn-ghost" onClick={() => setModalBloquesOpen(false)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="toast">✓ {toast}</div>}
    </div>
  );
}

function SlotBloque({ bloque }) {
  return (
    <div style={{ background: bloque.color + '14', borderLeft: `2.5px dashed ${bloque.color}`, borderRadius: 4, padding: '3px 7px', display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 10, color: bloque.color, fontWeight: 600, opacity: 0.7 }}>↻</span>
      <span style={{ fontSize: 10, color: bloque.color, fontWeight: 500, fontStyle: 'italic' }}>
        {bloque.titulo} · {bloque.hora_inicio.substring(0,5)}–{bloque.hora_fin.substring(0,5)}
      </span>
    </div>
  );
}

function SlotEvento({ ev, colorTipo, onEdit, onExpediente, sinHora }) {
  const color = colorTipo(ev.tipo);
  return (
    <div style={{ background: color + '14', borderLeft: `2.5px solid ${color}`, borderRadius: 4, padding: '4px 7px', cursor: 'pointer' }}
      onClick={() => onEdit(ev)}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
        {ev.hora && <span style={{ fontSize: 10, fontFamily: 'DM Mono', color, fontWeight: 600, flexShrink: 0 }}>{ev.hora.substring(0, 5)}</span>}
        <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text)', lineHeight: 1.3 }}>{ev.titulo}</span>
      </div>
      <div style={{ fontSize: 10, color, marginTop: 1 }}>{ev.tipo}</div>
      {ev.expedientes && (
        <div onClick={e => { e.stopPropagation(); onExpediente(ev.expediente_id); }} style={{ fontSize: 10, color: 'var(--blue)', marginTop: 2, cursor: 'pointer' }}>
          📁 {ev.expedientes.caratula?.substring(0, 30)}
        </div>
      )}
      {(ev.clientes || ev.cliente_manual) && (
        <div style={{ fontSize: 10, color: 'var(--text4)', marginTop: 1 }}>
          {ev.clientes ? `${ev.clientes.nombre} ${ev.clientes.apellido}` : ev.cliente_manual}
        </div>
      )}
    </div>
  );
}

function clientesDeExpediente(exp) {
  if (!exp?.expedientes_clientes?.length) return '';
  return exp.expedientes_clientes.map(ec => `${ec.clientes?.nombre || ''} ${ec.clientes?.apellido || ''}`.trim()).filter(Boolean).join(', ');
}
function clientesDeTramite(tr) {
  if (!tr?.tramites_clientes?.length) return '';
  return tr.tramites_clientes.map(tc => `${tc.clientes?.nombre || ''} ${tc.clientes?.apellido || ''}`.trim()).filter(Boolean).join(', ');
}

function SlotTarea({ tarea, prioridadColor, onCompletar, onExpediente, onTramite, onVerTarea, formatDate, today }) {
  const vencida = tarea.fecha_vencimiento && tarea.fecha_vencimiento < today;
  const clientes = tarea.expedientes ? clientesDeExpediente(tarea.expedientes) : clientesDeTramite(tarea.tramites);
  return (
    <div onClick={() => onVerTarea(tarea)} style={{ background: 'var(--surface2)', borderLeft: `2.5px solid ${prioridadColor(tarea.prioridad)}`, borderRadius: 4, padding: '5px 7px', display: 'flex', gap: 6, alignItems: 'flex-start', cursor: 'pointer' }}>
      <input type="checkbox" style={{ marginTop: 2, cursor: 'pointer', flexShrink: 0 }} onClick={e => e.stopPropagation()} onChange={() => onCompletar(tarea.id)} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text)', lineHeight: 1.3 }}>{tarea.titulo}</div>
        {tarea.expedientes && <div style={{ fontSize: 10, color: 'var(--text4)', marginTop: 2 }}>📁 {tarea.expedientes.caratula?.substring(0, 28)}</div>}
        {tarea.tramites && <div style={{ fontSize: 10, color: 'var(--text4)', marginTop: 2 }}>📋 {tarea.tramites.nombre?.substring(0, 28)}</div>}
        {clientes && <div style={{ fontSize: 10, color: 'var(--text4)', marginTop: 1 }}>👤 {clientes.substring(0, 30)}</div>}
        {tarea.fecha_vencimiento && <div style={{ fontSize: 9, color: vencida ? 'var(--red)' : 'var(--text4)', marginTop: 2 }}>⏰ {formatDate(tarea.fecha_vencimiento)}{vencida ? ' — VENCIDA' : ''}</div>}
      </div>
    </div>
  );
}
